import React, { useState, useEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { createSupabaseWithAuth } from '../../../../Components/Utils/SupabaseConfig';
import { useSession } from '../../../../Components/Utils/SessionContext';
import SearchableSelectUI from './SearchableSelectUI';

const RoleSearchSelect = ({
  label = "Select Role",
  placeholder = "Search for a role...",
  selectedRole,
  onRoleSelect,
  onClear,
  disabled = false,
  clearable = true,
  searchDelay = 300,
  limit = 10
}) => {
  const { user } = useSession();
  const [searchTerm, setSearchTerm] = useState(selectedRole?.subCategory || '');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Update search term when selectedRole changes
  useEffect(() => {
    if (selectedRole) {
      setSearchTerm(selectedRole.subCategory || '');
    }
  }, [selectedRole]);

  // Function to fetch role suggestions from Supabase
  const fetchRoleSuggestions = async (term) => {
    if (!term.trim() || term.length < 2) {
      setResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      // Check for user session
      if (!user || !user.access_token) {
        throw new Error('User session not available. Please log in again.');
      }
      
      // Create Supabase client with auth
      const supabaseWithAuth = createSupabaseWithAuth(user.access_token);
      
      // Make the search query
      const { data, error } = await supabaseWithAuth
        .from('role_categories')
        .select('category_id, subCategory, mainCategory')
        .ilike('subCategory', `%${term}%`)
        .eq('is_available', true)
        .limit(limit);
      
      if (error) {
        console.error('Role search error:', error);
        setResults([]);
        return;
      }
      
      if (!data || data.length === 0) {
        setResults([]);
        return;
      }
      
      // Format the results - keep the original data structure
      setResults(data);
    } catch (error) {
      console.error('Error searching for roles:', error);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Create debounced search function
  const debouncedFetchRoles = useMemo(
    () => debounce(fetchRoleSuggestions, searchDelay),
    [user, searchDelay]
  );
  
  // Handle search input change
  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    if (term.length >= 2) {
      debouncedFetchRoles(term);
    } else {
      setResults([]);
    }
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedFetchRoles.cancel();
    };
  }, [debouncedFetchRoles]);

  // Handle role selection
  const handleRoleSelect = (role) => {
    onRoleSelect(role);
    setSearchTerm(role.subCategory);
    setResults([]);
  };

  // Handle clear
  const handleClear = () => {
    onClear();
    setSearchTerm('');
    setResults([]);
  };

  return (
    <SearchableSelectUI
      id="roleSearch"
      label={label}
      placeholder={placeholder}
      searchTerm={searchTerm}
      onSearchChange={handleSearchChange}
      results={results}
      isSearching={isSearching}
      selectedItem={selectedRole}
      onItemSelect={handleRoleSelect}
      onClear={handleClear}
      disabled={disabled}
      clearable={clearable}
      renderItem={(role) => (
        <>
          <div className="font-medium">{role.subCategory}</div>
          {role.mainCategory && <div className="text-sm text-gray-600">{role.mainCategory}</div>}
        </>
      )}
      renderSelectedItem={(role) => (
        <>
          <div className="font-medium">{role.subCategory}</div>
          {role.mainCategory && <div className="text-sm text-gray-600">{role.mainCategory}</div>}
        </>
      )}
    />
  );
};

export default RoleSearchSelect;
